/**
 * **文档库** —— 这个编辑器里"有哪些文档"的单一来源（纯逻辑 + 落盘）。
 *
 * ```
 *   DocumentRecord   一份文档：UUID + 名字 + 图 + 工具设置 + 时间戳
 *   LibraryState     { version, documents }  ← 整份存进 localStorage
 * ```
 *
 * ## 为什么是 localStorage（而不是文件 / IndexedDB）
 *
 * 用户拍板：「浏览器本地 localStorage（推荐）」。理由很实在 ——
 * 节点包 / 主题 / 窗口布局都住在那里，这是**同一个宿主**里同一类东西。
 * 代价说清楚：**约 5MB**，而图片是以 base64 内嵌在文档里的（见
 * `types/widget.ts` 的 `ImageWidget`）—— 大文档会撑爆配额。
 * 所以 `writeLibrary` **不静默失败**：配额满要报出来（`{ ok: false }`），
 * 由界面告诉用户"存不下了，导出成文件吧"。静默丢掉一份文档是最坏的结果。
 *
 * ## ⚠️ 为什么这些函数都收一个 `storage` 参数
 *
 * 为了**能被单测覆盖**。这套东西的坑全在"存坏了怎么办 / 配额满了怎么办 /
 * UUID 撞了怎么办"上，而它们只有在能喂假 storage 的时候才测得了
 * （`verify:documents` 就是拿一个会抛 QuotaExceededError 的假 storage 验的）。
 *
 * ## ⚠️ 文档的 UUID 是**在文件里跟着走的**
 *
 * `TemplateFile.id` 就是它。所以"从文件里读进来一份文档，库里已经有一份
 * 同 UUID 的"这件事**认得出**（用户要的正是这个：弹窗问是否覆盖）。
 * 老文件没有 id —— 那就当新文档（生成一个新 UUID），不问。
 */
import type { GraphDocument } from './serialization'
import type { TemplateFile } from './templateFile'
import { TEMPLATE_KIND, TEMPLATE_VERSION } from './templateFile'
import type {
  DocumentBody,
  DocumentRecord,
  LibraryState,
  LibraryStorage,
  ToolSettings,
} from '@/types/document'

/*
 * ⚠️ 形状住在 `types/document.ts`（视觉层也读它），算法在这儿 ——
 * 这里重新导出一次，方便只关心"落盘"的调用方一次 import 拿全。
 */
export type {
  DocumentBody,
  DocumentRecord,
  LibraryState,
  LibraryStorage,
  ToolSettings,
} from '@/types/document'

/** 落盘用的键（带 v1：以后换形状时可以并存，不用猜旧数据是什么） */
export const LIBRARY_KEY = 'node-print.documents.v1'
export const LIBRARY_VERSION = 1

/* ══════════════════════════════════════════════════════════════════
   UUID
   ══════════════════════════════════════════════════════════════════ */

/**
 * 一个新的文档 UUID（v4 形状）。
 *
 * ⚠️ 三层兜底，每一层都有理由：
 *   ① `crypto.randomUUID()` —— 现代浏览器 + 安全上下文，首选；
 *   ② `crypto.getRandomValues()` —— 有 crypto 但没有 randomUUID
 *      （旧 Safari / 非安全上下文）；
 *   ③ `Math.random()` —— 连 crypto 都没有（**happy-dom 就没有**，
 *      而整套验证脚本跑在它上面：不留这一层，测试里生成的 id 是 `undefined`，
 *      而"文档没有 id"这种 bug 会一路滑到线上）。
 */
export function newDocumentId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()

  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  /* 版本位 / 变体位 —— 让它长得像 v4（外部系统可能按 UUID 校验） */
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/* ══════════════════════════════════════════════════════════════════
   名字 / 摘要
   ══════════════════════════════════════════════════════════════════ */

/** 没名字的文档也要有个能点的名字（空字符串在列表里是一个看不见的行） */
export const UNNAMED_DOCUMENT = '未命名文档'

export function safeDocumentName(name: unknown): string {
  if (typeof name !== 'string') return UNNAMED_DOCUMENT
  const trimmed = name.trim()
  return trimmed === '' ? UNNAMED_DOCUMENT : trimmed
}

/** 列表上那句"多大"：节点 / 边（**不算**字节数 —— 那个数对用户没有意义） */
export function documentSize(document: DocumentBody | undefined): { nodes: number; edges: number } {
  return {
    nodes: Array.isArray(document?.nodes) ? document!.nodes.length : 0,
    edges: Array.isArray(document?.edges) ? document!.edges.length : 0,
  }
}

/* ══════════════════════════════════════════════════════════════════
   读 / 写
   ══════════════════════════════════════════════════════════════════ */

export function emptyLibrary(): LibraryState {
  return { version: LIBRARY_VERSION, documents: [] }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 一条记录是不是能用（形状不对的**丢掉**，而不是让整个库读不出来） */
function normalizeRecord(raw: unknown): DocumentRecord | null {
  if (!isRecord(raw)) return null
  const document = raw.document
  if (!isRecord(document) || !Array.isArray(document.nodes) || !Array.isArray(document.edges)) {
    return null
  }
  const id = typeof raw.id === 'string' && raw.id !== '' ? raw.id : newDocumentId()
  const now = Date.now()
  return {
    id,
    name: safeDocumentName(raw.name),
    /* 形状在 `types/document.ts` 是最小集，这里是完整契约 —— 显式转一次 */
    document: document as unknown as GraphDocument,
    ...(isRecord(raw.tool) ? { tool: raw.tool as unknown as ToolSettings } : {}),
    createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : now,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : now,
  }
}

/**
 * 解析库里那份 JSON。
 *
 * ⚠️ **坏数据只丢那一条，不丢整个库**：一份文档的形状不对
 * （手改过 / 写到一半断电）不该让用户**其余所有文档**都消失。
 * 「半可用比整个消失好排查」是这个仓库一贯的口径。
 */
export function parseLibrary(raw: string | null): LibraryState {
  if (!raw) return emptyLibrary()
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return emptyLibrary()
  }
  if (!isRecord(parsed) || !Array.isArray(parsed.documents)) return emptyLibrary()
  const documents: DocumentRecord[] = []
  const seen = new Set<string>()
  for (const item of parsed.documents) {
    const record = normalizeRecord(item)
    if (!record) continue
    /*
     * ⚠️ 库里出现重复 UUID：**后来的让位**（换一个新 id）而不是丢掉 ——
     * 用户的数据比"id 好不好看"重要。这种情况只可能来自手改过的存储。
     */
    if (seen.has(record.id)) record.id = newDocumentId()
    seen.add(record.id)
    documents.push(record)
  }
  const lastId = typeof parsed.lastId === 'string' && parsed.lastId !== '' ? parsed.lastId : null
  return { version: LIBRARY_VERSION, documents, lastId }
}

export function serializeLibrary(state: LibraryState): string {
  return JSON.stringify({
    version: LIBRARY_VERSION,
    documents: state.documents,
    /* ⚠️ 空值不写出去（老读法看到 `null` 会当成一个真的 id 去查一遍） */
    ...(state.lastId ? { lastId: state.lastId } : {}),
  })
}

export function readLibrary(storage: LibraryStorage | undefined): LibraryState {
  if (!storage) return emptyLibrary()
  try {
    return parseLibrary(storage.getItem(LIBRARY_KEY))
  } catch {
    /* 读不出来当没有（隐私模式 / 被禁用）—— 不该让编辑器起不来 */
    return emptyLibrary()
  }
}

/** 落盘失败的原因（配额 / 被禁用）—— **必须能报到界面上**，见文件头 */
export interface WriteFailure {
  ok: false
  message: string
}

export type WriteResult = { ok: true } | WriteFailure

export function writeLibrary(
  state: LibraryState,
  storage: LibraryStorage | undefined,
): WriteResult {
  if (!storage) return { ok: false, message: '这个环境没有 localStorage —— 文档存不下来' }
  try {
    storage.setItem(LIBRARY_KEY, serializeLibrary(state))
    return { ok: true }
  } catch (error) {
    const name = (error as { name?: string })?.name ?? ''
    if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      return {
        ok: false,
        message:
          '浏览器给这个站点分配的存储满了，这份文档没能存下来。' +
          '先把它导出成文件（导出），再删掉库里的几份旧文档腾地方。',
      }
    }
    return { ok: false, message: `存不进去：${(error as Error)?.message ?? String(error)}` }
  }
}

/* ══════════════════════════════════════════════════════════════════
   纯函数：增删改查（都返回**新状态**，不改传进来的那份）
   ══════════════════════════════════════════════════════════════════ */

export function findDocument(state: LibraryState, id: string | null | undefined): DocumentRecord | undefined {
  if (!id) return undefined
  return state.documents.find((item) => item.id === id)
}

/** 列表顺序：**最近改过的在前**（用户最可能找的就是刚才那份） */
export function sortedDocuments(state: LibraryState): DocumentRecord[] {
  return [...state.documents].sort((a, b) => b.updatedAt - a.updatedAt)
}

/**
 * 插入或**按 id 覆盖**。
 *
 * ⚠️ 覆盖时 `createdAt` **保留原来的**：它记的是"这份文档什么时候有的"，
 * 不该因为一次保存就变成今天。而 `updatedAt` 一律刷新。
 */
export function upsertDocument(state: LibraryState, record: DocumentRecord): LibraryState {
  const index = state.documents.findIndex((item) => item.id === record.id)
  if (index < 0) return { ...state, documents: [...state.documents, record] }
  const documents = [...state.documents]
  documents[index] = { ...record, createdAt: state.documents[index]!.createdAt }
  return { ...state, documents }
}

export function renameDocument(state: LibraryState, id: string, name: string): LibraryState {
  return {
    ...state,
    documents: state.documents.map((item) =>
      item.id === id ? { ...item, name: safeDocumentName(name), updatedAt: Date.now() } : item,
    ),
  }
}

export function removeDocument(state: LibraryState, id: string): LibraryState {
  return { ...state, documents: state.documents.filter((item) => item.id !== id) }
}

/**
 * 复制一份（新 UUID、新名字、新时间戳）。
 *
 * ⚠️ 图要**深拷贝**：两份文档共享同一个 `document` 对象的话，
 * 改一份会连另一份一起改（模板文件那条路上踩过同类的坑）。
 */
export function duplicateDocument(
  state: LibraryState,
  id: string,
  name?: string,
): { state: LibraryState; record?: DocumentRecord } {
  const source = findDocument(state, id)
  if (!source) return { state }
  const now = Date.now()
  const record: DocumentRecord = {
    ...source,
    id: newDocumentId(),
    name: safeDocumentName(name ?? `${source.name} 副本`),
    document: JSON.parse(JSON.stringify(source.document)) as GraphDocument,
    ...(source.tool ? { tool: { ...source.tool } } : {}),
    createdAt: now,
    updatedAt: now,
  }
  return { state: { ...state, documents: [...state.documents, record] }, record }
}

/* ══════════════════════════════════════════════════════════════════
   与模板文件互转
   ══════════════════════════════════════════════════════════════════ */

/** 一份记录 → 一个模板文件（导出 / 落盘共用一处，免得两边漏字段） */
export function recordToFile(record: DocumentRecord): TemplateFile {
  return {
    kind: TEMPLATE_KIND,
    version: TEMPLATE_VERSION,
    id: record.id,
    name: record.name,
    document: JSON.parse(JSON.stringify(record.document)) as GraphDocument,
    ...(record.tool ? { tool: { ...record.tool } } : {}),
  }
}

/**
 * 一份文件 → 一条记录。
 *
 * ⚠️ 文件里**没有 id** 时生成一个新的：老模板（这个字段是后加的）导进来
 * 也该出现在库里，而不是被拒之门外。而"要不要覆盖已有的那份"这件事
 * 得由调用方按**文件里到底有没有 id** 来判断 —— 见 `importConflictOf`。
 */
export function fileToRecord(file: TemplateFile, fallbackName: string): DocumentRecord {
  const now = Date.now()
  return {
    id: typeof file.id === 'string' && file.id !== '' ? file.id : newDocumentId(),
    name: safeDocumentName(file.name ?? fallbackName),
    document: file.document,
    ...(file.tool ? { tool: file.tool } : {}),
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * ★★ **UUID 撞车**：这份文件是不是库里已经有的那一份？
 *
 * 判据是**文件里带着 id，而且那个 id 在库里**（用户要的原话：
 * 「加载文档的时候，如果发现 UUID 有重复的时候弹窗提醒是否覆盖」）。
 *
 * ⚠️ 老文件（没有 id）**永远不算撞车** —— 那时候 id 是我们生成的，
 * 拿它去比对等于"每次都撞"，会把每个导入都变成一个弹窗。
 */
export function importConflictOf(
  state: LibraryState,
  file: TemplateFile,
): DocumentRecord | undefined {
  if (typeof file.id !== 'string' || file.id === '') return undefined
  return findDocument(state, file.id)
}
